/* eslint-disable @typescript-eslint/no-explicit-any */
import PackageActions from "./PackageAtions";
import { useModal } from "@/app/shared/modal-views/use-modal";
import { PackageForm } from "./PackageForm";
import PackageDelete from "./PackageDelete";
import { IoMdAdd } from "react-icons/io";
import toast from "react-hot-toast";
import PackageRepository from "@/utilities/repositories/Package";
import { useParams } from "react-router-dom";
import { useState } from "react";
import { IPackage, IUpdateUserPackageDTO } from "@/utilities/types/Package";
import dayjs from "dayjs";

interface PackageCardProps {
  pkg: IPackage;
  userPackageId?: string;
  userPkgExpiredAt?: Date | null;
  callback: (...arg: any) => void;
  isRoot?: boolean;
}

const PackageCard: React.FC<PackageCardProps> = ({
  pkg,
  userPackageId = "",
  userPkgExpiredAt = null,
  callback,
  isRoot = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { openModal, closeModal } = useModal();
  const { id: userId = "" } = useParams();

  const handleAdd = async () => {
    try {
      setIsLoading(true);
      const res = await PackageRepository.createUserPackage({
        userId: userId || "",
        packageId: pkg.id,
      });
      const data = res?.data || {};
      const newPkg = {
        ...pkg,
        expiredAt: data.expiredAt,
      };
      callback({
        pkg: newPkg,
        userPkgId: data.id,
      });
      toast.success("Package added successfully");
    } catch (error: any) {
      toast.error(error?.message || "Error adding package");
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async (data: { expiredAt: Date | null }) => {
    const userPkgUpdateData: IUpdateUserPackageDTO = {
      packageId: pkg.id,
      expiredAt: data.expiredAt?.toISOString() || null,
      enabled: true,
    };
    try {
      const res = await PackageRepository.updateUserPackage(
        userPackageId,
        userPkgUpdateData
      );

      const data = res?.data || {};

      callback({
        pkg: data.package,
        userPkgId: data.id,
        expiredAt: data.expiredAt,
      });
      closeModal();
      toast.success("Package updated successfully");
    } catch (error: any) {
      toast.error(error?.message || "Error updating package");
    }
  };

  const handleDelete = async () => {
    try {
      await PackageRepository.deleteUserPackage(userPackageId);
      callback();
      closeModal();
      toast.success("Package deleted successfully");
    } catch (error: any) {
      toast.error(error?.message || "Error deleting package");
    }
  };

  const openModalDelete = () => {
    openModal({
      view: (
        <PackageDelete
          closeModal={closeModal}
          packageName={pkg.name}
          onDelete={handleDelete}
        />
      ),
      customSize: "300px",
    });
  };

  const openModalEdit = () => {
    openModal({
      view: (
        <PackageForm
          pkg={pkg}
          onSubmit={onSave}
          onClose={closeModal}
          userPkgExpiredAt={userPkgExpiredAt}
        />
      ),
    });
  };

  return (
    <div className="border rounded-lg p-4 shadow-md bg-white space-y-2 min-w-[250px] max-w-fit">
      <div className="w-full flex flex-row justify-between">
        <h6 className="text-md text-ellipsis font-bold self-center">
          {pkg.name}
        </h6>
        {!userPackageId ? (
          <button
            type="button"
            onClick={handleAdd}
            disabled={isLoading}
            className="hover:bg-gray-300 p-2 rounded-full"
          >
            <IoMdAdd
              size={24}
              className="cursor-pointer"
              title="add new package"
            />
          </button>
        ) : (
          <PackageActions
            handleDelete={openModalDelete}
            handleEdit={openModalEdit}
          />
        )}
      </div>
      {pkg.description && <p className="text-gray-600">{pkg.description}</p>}
      <p>
        <strong>Price:</strong> ${pkg.price}
      </p>
      {(userPkgExpiredAt || pkg.expiredAt) && pkg.duration && (
        <p>
          <strong>Duration:</strong> {pkg.duration}{" "}
          {Number(pkg.duration) > 1 && pkg.durationType
            ? `${pkg.durationType}s`.toLowerCase()
            : `${pkg.durationType}`.toLowerCase() || "days"}
        </p>
      )}
      {pkg.discount && (
        <p>
          <strong>Discount:</strong> {Math.round(pkg.discount)}%
        </p>
      )}
      {!isRoot && (
        <p>
          <strong>Expired at: </strong>
          {pkg.expiredAt || userPkgExpiredAt
            ? dayjs(pkg.expiredAt || userPkgExpiredAt).format("DD/MM/YYYY")
            : "lifetime"}
        </p>
      )}
    </div>
  );
};

export default PackageCard;
